import { useEffect } from "react";
import * as Tone from "tone";
import { ChordContext } from "./harmonyEngine";
import { SampleKit } from "./useSamples";

const HUMANIZE_S = 0.012;
const NOTE_NAMES = ['C','Db','D','Eb','E','F','F#','G','Ab','A','Bb','B'];

function jitter() {
  return (Math.random() - 0.5) * HUMANIZE_S;
}

function midiToNote(midi: number): string {
  return `${NOTE_NAMES[midi % 12]}${Math.floor(midi / 12) - 1}`;
}

// Anchor chord root to C2–B2 (MIDI 36–47) for a warm lofi bass register.
function bassRootMidi(keyRootMidi: number, semitones: number): number {
  let midi = keyRootMidi + semitones;
  while (midi > 47) midi -= 12;
  while (midi < 36) midi += 12;
  return midi;
}

export function useBass(
  samples: SampleKit | null,
  chordContextRef: { current: ChordContext | null },
  configRef: { current: { enabled: boolean } },
): void {
  useEffect(() => {
    if (!samples) return;

    const id = Tone.Transport.scheduleRepeat((time) => {
      if (!configRef.current.enabled) return;
      const ctx = chordContextRef.current;
      if (!ctx) return;

      const root = bassRootMidi(ctx.keyRootMidi, ctx.degree.semitones);
      const fifth = root + 7;
      const eighth = Tone.Time("8n").toSeconds();

      const rootNote  = midiToNote(root);
      const fifthNote = midiToNote(fifth);

      // Beat 1: root — always
      samples.piano.triggerAttackRelease(rootNote, "4n", time + jitter());

      // Beat 3 (4 eighth notes in): fifth — mostly
      if (Math.random() < 0.50)
        samples.piano.triggerAttackRelease(fifthNote, "8n", time + eighth * 4 + jitter());

      // Beat 4 "and" (6 eighth notes in): chromatic approach into next chord
      if (Math.random() < 0.25) {
        const approach = midiToNote(root + (Math.random() < 0.7 ? -1 : 1));
        samples.piano.triggerAttackRelease(approach, "8n", time + eighth * 6 + jitter());
      }
    }, "1m");

    return () => {
      Tone.Transport.clear(id);
    };
  }, [samples, chordContextRef, configRef]);
}
